/**
 * Canonical Instagram / TikTok handle normalizer.
 *
 * WHY THIS EXISTS
 * ---------------
 * Users paste full share URLs into the "username" field. Nothing extracted the
 * handle, so raw URLs were persisted to `setup_sessions.username` and later
 * interpolated into marketing subject lines ("final note about
 * @https://www.instagram.com/stories/sehjad_ali_21/39"). Worse, the paste
 * handlers on the setup search fields filtered the clipboard down to
 * `[a-zA-Z0-9_.]`, which MANGLED a URL into run-together garbage
 * ("httpswww.instagram.comtrade_with_yannfxstknMTR5MDdlbzhxM21udQ") instead of
 * extracting anything.
 *
 * This module is the ONE place that turns arbitrary user input into a handle.
 * Every write path that accepts a handle must run it and must reject — never
 * silently truncate, never guess.
 *
 * CONTRACT
 * --------
 * - Accepts: plain handles, `@handle`, handles with stray whitespace /
 *   zero-width characters / smart quotes / a trailing slash, and profile URLs
 *   in every common shape (with or without protocol, with or without `www.`/
 *   `m.`, with arbitrary query strings).
 * - Rejects (with a typed reason + a user-facing message): URLs that carry no
 *   handle at all (`/p/<id>`, `/reel/<id>`, `/explore/...`, shortlinks),
 *   mangled URLs whose handle boundary cannot be determined, links for the
 *   other platform, and anything that breaks the platform's charset/length
 *   rules.
 * - Case: `handle` preserves what the user typed (display); `normalized` is the
 *   lowercase form and is the ONLY value that may be compared, deduped or used
 *   as a cache/Redis key. This matches the existing lowercase convention in
 *   `src/app/[locale]/(landing)/instagram/[username]/_lib/types.ts`.
 */

export type SocialPlatform = "instagram" | "tiktok"

export type HandleRejectionReason =
    /** Nothing usable was supplied (empty / whitespace / not a string). */
    | "empty"
    /** Input is absurdly long — not a handle, not a URL we will parse. */
    | "input_too_long"
    /** A recognised profile-host URL that points at content, not a profile. */
    | "no_handle_in_url"
    /** An already-mangled URL whose handle boundary cannot be recovered. */
    | "ambiguous_url"
    /** Looks like a URL but is not an Instagram/TikTok profile URL we can read. */
    | "unsupported_url"
    /** An Instagram link submitted as TikTok, or vice-versa. */
    | "platform_mismatch"
    | "too_short"
    | "too_long"
    | "invalid_characters"

// NOTE: the repo compiles with `strictNullChecks: false`, which disables
// discriminated-union narrowing on a boolean tag. The `?: undefined` members
// keep every field reachable on the union under the current config AND keep
// real narrowing if strict mode is ever switched on.
export interface HandleAccepted {
    ok: true
    /** Handle as the user cased it — for display only. */
    handle: string
    /** Lowercased handle — compare / dedupe / key on THIS, never on `handle`. */
    normalized: string
    /** True when the handle had to be extracted out of a (possibly mangled) URL. */
    extractedFromUrl: boolean
    reason?: undefined
    message?: undefined
}

export interface HandleRejected {
    ok: false
    handle?: undefined
    normalized?: undefined
    extractedFromUrl?: undefined
    reason: HandleRejectionReason
    /** Actionable, user-facing. Safe to render or return in an API error verbatim. */
    message: string
}

export type HandleResult = HandleAccepted | HandleRejected

// ── Platform rules ────────────────────────────────────────────────────────────
// Instagram: 1–30 chars, letters/digits/period/underscore. Meta publishes no
//            numeric limit; the ceiling is corroborated by all 15,444 Instagram
//            rows in `accounts` (max length 30, zero charset violations).
// TikTok:    2–24 chars, letters/digits/period/underscore, may not END with a
//            period — stated verbatim on support.tiktok.com.
// DELIBERATELY NOT ENFORCED: the widely-repeated "no leading period / no
// consecutive periods" rules. They hold for Instagram in our data (0/15,444
// violations) but TikTok demonstrably allows both (5 leading-dot and 1
// double-dot live accounts), and blocking a real signup costs revenue.
export const HANDLE_RULES: Record<SocialPlatform, { min: number; max: number; charset: RegExp; noTrailingDot: boolean; label: string }> = {
    instagram: { min: 1, max: 30, charset: /^[A-Za-z0-9._]+$/, noTrailingDot: false, label: "Instagram" },
    tiktok: { min: 2, max: 24, charset: /^[A-Za-z0-9._]+$/, noTrailingDot: true, label: "TikTok" }
}

const MAX_INPUT_LENGTH = 2048

/** Zero-width, soft hyphen, BOM, word joiner, bidi controls. */
const INVISIBLE_CHARS = /[­᠎​-‏‪-‮⁠-⁤⁦-⁩﻿]/g
/** Straight + smart quotes, backticks and angle quotes users wrap pastes in. */
const WRAPPING_QUOTES = /^[\s"'`‘’‚‛“”„‟«»‹›]+|[\s"'`‘’‚‛“”„‟«»‹›]+$/g

const INSTAGRAM_HOSTS = new Set(["instagram.com", "instagr.am"])
const TIKTOK_HOSTS = new Set(["tiktok.com"])
/** Hosts that only ever carry an opaque redirect code — never a handle. */
const TIKTOK_SHORTLINK_SUBDOMAINS = new Set(["vm", "vt"])

/**
 * First path segments on instagram.com that are product routes, never handles.
 * Instagram publishes no reserved list, so this is a prudent superset built from
 * the live route set (`/share/` is Meta's newer share-shortlink namespace and is
 * common in the wild).
 */
const INSTAGRAM_RESERVED = new Set([
    "p", "reel", "reels", "tv", "igtv", "explore", "s", "share", "stories",
    "accounts", "direct", "challenge", "oauth", "web", "developer", "developers",
    "legal", "about", "privacy", "api", "graphql", "emails", "session", "ajax",
    "your_activity", "create", "lite", "invites", "help", "press", "blog",
    "download", "topics", "qr", "nametag", "l"
])

/**
 * First path segments on tiktok.com that are product routes, never handles.
 * The bulk is first-party verified from https://www.tiktok.com/robots.txt.
 * TikTok profiles are always `@`-prefixed, so this list is belt-and-braces.
 */
const TIKTOK_RESERVED = new Set([
    "t", "video", "photo", "tag", "music", "discover", "foryou", "explore",
    "search", "live", "upload", "following", "friends", "about", "legal",
    "business", "ads", "effect", "node", "channel", "trending", "embed",
    "login", "signup", "setting", "creators", "privacy", "safety", "tiktokstudio",
    "share", "auth", "link", "inapp", "sgtm", "directory", "amp", "transparency",
    "forgood", "community-guidelines", "shop", "api"
])

/**
 * Query-parameter names that survive the legacy `[a-zA-Z0-9_.]` paste filter as
 * a run-together suffix. Only DISTINCTIVE keys are listed: short keys like `hl`
 * or `si` occur inside real handles ("ashley" contains "hl") and splitting on
 * them would invent a handle, which this module must never do.
 */
const MANGLED_TRACKING_KEYS = ["igshid", "igsh", "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid", "mibextid", "stkn", "share_app_id", "share_link_id"]

/**
 * Content routes long enough to be a reliable signal inside a MANGLED url.
 * Short reserved routes ("p", "s", "tv", "t") are deliberately excluded — they
 * prefix countless real handles.
 */
/**
 * Tracking keys that also occur INSIDE real handles, so they only mark the end of
 * a handle when what follows looks like an actual share token. Instagram's `igsi`
 * appears in live handles "wigsinanhour" and "bigsisterteatalk", where the tail
 * ("nanhour", "sterteatalk") is plain lowercase; a genuine token is base64 — 8+
 * chars carrying both an uppercase letter and a digit. Unconditional keys stay in
 * MANGLED_TRACKING_KEYS; do NOT move a key here without checking the live corpus.
 */
const MANGLED_CONDITIONAL_KEYS = ["igsi"]

function looksLikeShareToken(suffix: string): boolean {
    // Base64url share tokens are 8+ alphanumerics and always carry an uppercase
    // letter; a digit is common but NOT guaranteed ("aTIyNHFuOXdjZWZq"), so
    // requiring one leaves junk behind. The live handles that contain "igsi" fail
    // this on other grounds: "wigsinanhour" -> "nanhour" is 7 chars, and
    // "bigsisterteatalk" -> "sterteatalk" has no uppercase.
    return /^[A-Za-z0-9]{8,}$/.test(suffix) && /[A-Z]/.test(suffix)
}

const MANGLED_CONTENT_PREFIXES = ["stories", "explore", "accounts", "challenge", "oauth", "developer", "foryou", "following", "friends"]

/**
 * Deliberately NOT in the list above:
 *  - "video", "photo", "music", "direct", "discover", "search", "upload" are ordinary
 *    English word-starts. Rejecting on them refused live handles like
 *    "photography_as_199", "musicfromscotland" and "directedbynr".
 *  - "reel"/"reels"/"tv"/"p" are handled by MANGLED_CONTENT_SHORTCODE_RE instead, which
 *    keys off the 11-char shortcode rather than the word, so "reelcatch0" survives while
 *    "reelDVS1YuGDHe2" is refused.
 *  - "stories" IS still listed: MANGLED_STORY_RE runs first and rescues the real
 *    `stories<handle><19-digit id>` shape, and anything else beginning with "stories"
 *    is refused rather than guessed at.
 */

/**
 * A mangled story link: `instagram.com/stories/<handle>/<19-digit id>` arrives as
 * `stories<handle><id>`. The id is a long unbroken digit run, so the handle is the
 * text between "stories" and that run. A real handle that merely begins with
 * "stories" ("stories_of_gvantsa") has no such trailing digit run and is untouched.
 */
const MANGLED_STORY_RE = /^stories([A-Za-z0-9._]*?)([0-9]{15,})$/i

/**
 * `httpswww.instagram.com...` — a URL AFTER the legacy paste filter ate the
 * separators. The remainder must itself be separator-free, otherwise this is a
 * normal protocol-less URL ("www.instagram.com/outfame?igsh=x") and belongs in
 * the real-URL branch.
 */
/**
 * A short reserved route ("p", "reel", "tv") glued to an Instagram/TikTok content
 * shortcode by the paste filter: `instagram.com/p/DTGB_1DkYrv` -> `pDTGB_1DkYrv`.
 *
 * "p" and "tv" are too short to list in MANGLED_CONTENT_PREFIXES — they open
 * countless real handles ("pastor_ezekielolowe", "pyro.rock1"). The shortcode
 * ITSELF is the reliable signal: 10-12 chars of [A-Za-z0-9_-], carrying an
 * uppercase letter and a digit/underscore/dash, and never a dot. Verified against
 * 15,843 live accounts and 97,707 influencer handles: zero real handles match.
 */
const MANGLED_CONTENT_SHORTCODE_RE = /^(?:p|reels?|tv)([A-Za-z0-9_-]{10,12})$/

const MANGLED_URL_RE = /^(?:https?)?(?:www\.|m\.|l\.)?(instagram\.com|instagr\.am|tiktok\.com)([A-Za-z0-9._@-]*)$/i

function reject(reason: HandleRejectionReason, message: string): HandleRejected {
    return { ok: false, reason, message }
}

/** The friendly catch-all the setup UI shows for any "that's a link" rejection. */
export function handleInputHint(platform: SocialPlatform): string {
    return `Enter your ${HANDLE_RULES[platform].label} username, like "outfame" — not a link.`
}

function cleanInput(raw: string): string {
    return raw
        .normalize("NFKC")
        .replace(INVISIBLE_CHARS, "")
        .replace(/[\r\n\t]+/g, "")
        .trim()
        .replace(WRAPPING_QUOTES, "")
        .replace(/\/+$/, "")
        .trim()
}

function validateCandidate(candidate: string, platform: SocialPlatform, extractedFromUrl: boolean): HandleResult {
    const rules = HANDLE_RULES[platform]
    const value = candidate.replace(/\/+$/, "")

    if (!value) return reject("empty", `Enter your ${rules.label} username.`)
    if (!rules.charset.test(value)) {
        return reject("invalid_characters", `${handleInputHint(platform)} Usernames only contain letters, numbers, periods and underscores.`)
    }
    if (value.length < rules.min) return reject("too_short", `That ${rules.label} username is too short — it needs at least ${rules.min} characters.`)
    if (value.length > rules.max) return reject("too_long", `That ${rules.label} username is too long — the maximum is ${rules.max} characters.`)
    if (rules.noTrailingDot && value.endsWith(".")) {
        return reject("invalid_characters", `A ${rules.label} username can't end with a period.`)
    }

    return { ok: true, handle: value, normalized: value.toLowerCase(), extractedFromUrl }
}

function decodeSegment(value: string): string | null {
    try {
        return decodeURIComponent(value)
    } catch {
        return null
    }
}

function hostPlatform(hostname: string): { platform: SocialPlatform; subdomain: string } | null {
    const host = hostname.toLowerCase().replace(/^\.+|\.+$/g, "")
    const parts = host.split(".")
    for (let i = 0; i < parts.length - 1; i++) {
        const base = parts.slice(i).join(".")
        const subdomain = parts.slice(0, i).join(".")
        if (INSTAGRAM_HOSTS.has(base)) return { platform: "instagram", subdomain }
        if (TIKTOK_HOSTS.has(base)) return { platform: "tiktok", subdomain }
    }
    return null
}

function extractFromInstagramPath(segments: string[], platform: SocialPlatform): HandleResult {
    if (segments.length === 0) return reject("no_handle_in_url", handleInputHint(platform))

    const first = segments[0].toLowerCase()

    if (first === "stories") {
        // instagram.com/stories/<handle>/<storyId> — the handle is the FIRST
        // segment after /stories/. /stories/highlights/<id> carries no handle.
        const candidate = segments[1] ? decodeSegment(segments[1]) : null
        if (!candidate || candidate.toLowerCase() === "highlights") {
            return reject("no_handle_in_url", `That story link doesn't contain a username. ${handleInputHint(platform)}`)
        }
        return validateCandidate(candidate.replace(/^@+/, ""), platform, true)
    }

    if (INSTAGRAM_RESERVED.has(first)) {
        return reject("no_handle_in_url", `That link points at a post, not a profile — it doesn't contain a username. ${handleInputHint(platform)}`)
    }

    const candidate = decodeSegment(segments[0])
    if (!candidate) return reject("no_handle_in_url", handleInputHint(platform))
    return validateCandidate(candidate.replace(/^@+/, ""), platform, true)
}

function extractFromTikTokPath(segments: string[], subdomain: string, platform: SocialPlatform): HandleResult {
    if (TIKTOK_SHORTLINK_SUBDOMAINS.has(subdomain)) {
        return reject("no_handle_in_url", `That short link doesn't contain a username. ${handleInputHint(platform)}`)
    }
    if (segments.length === 0) return reject("no_handle_in_url", handleInputHint(platform))

    // TikTok profiles are ALWAYS /@handle (optionally behind a locale segment).
    for (const segment of segments) {
        const decoded = decodeSegment(segment)
        if (decoded && decoded.startsWith("@") && decoded.length > 1) {
            return validateCandidate(decoded.replace(/^@+/, ""), platform, true)
        }
    }

    const first = segments[0].toLowerCase()
    if (TIKTOK_RESERVED.has(first)) {
        return reject("no_handle_in_url", `That link points at content, not a profile — it doesn't contain a username. ${handleInputHint(platform)}`)
    }
    return reject("no_handle_in_url", `That link doesn't contain a username. ${handleInputHint(platform)}`)
}

/**
 * Recover a handle from a URL the legacy paste filter already mangled
 * (`httpswww.instagram.comtrade_with_yannfxstknMTR5MDdlbzhxM21udQ`).
 * Returns a rejection whenever the handle's end boundary is not determinable —
 * guessing here would invent a username.
 */
function recoverMangled(rest: string, platform: SocialPlatform): HandleResult {
    let remainder = rest.replace(/^\.+/, "")
    if (!remainder) return reject("no_handle_in_url", handleInputHint(platform))

    if (platform === "tiktok") {
        remainder = remainder.replace(/^@+/, "")
    }

    const lower = remainder.toLowerCase()

    // Mangled story link: pull the author's handle out from in front of the story id.
    const story = remainder.match(MANGLED_STORY_RE)
    if (story) {
        const author = story[1]
        if (!author) return reject("no_handle_in_url", `That story link doesn't contain a username. ${handleInputHint(platform)}`)
        return validateCandidate(author, platform, true)
    }
    // Only DISTINCTIVE (>=4 char) content prefixes are checked here: short
    // reserved routes like "p", "s" or "tv" are the opening letters of countless
    // real handles, and rejecting on those would break normal users.
    if (MANGLED_CONTENT_PREFIXES.some((prefix) => lower.startsWith(prefix) && lower.length > prefix.length)) {
        return reject("ambiguous_url", `We couldn't read a username out of that link. ${handleInputHint(platform)}`)
    }

    // Split at the first DISTINCTIVE tracking key — everything before it is the
    // handle. A key at position 0 means the link had no handle at all
    // ("instagram.com/?igsh=..."), which is not recoverable.
    let cut = remainder.length
    for (const key of MANGLED_TRACKING_KEYS) {
        const at = lower.indexOf(key)
        if (at === 0) return reject("ambiguous_url", `We couldn't read a username out of that link. ${handleInputHint(platform)}`)
        if (at > 0 && at < cut) cut = at
    }
    // Ambiguous keys cut only when a real share token follows them.
    for (const key of MANGLED_CONDITIONAL_KEYS) {
        const at = lower.indexOf(key)
        if (at <= 0 || at >= cut) continue
        // Test only up to the current cut: a later unconditional key (…igsiTOKENutm_sourceqr)
        // would otherwise drag non-token characters into the check and suppress the cut.
        if (looksLikeShareToken(remainder.slice(at + key.length, cut))) cut = at
    }

    const candidate = remainder.slice(0, cut)

    // `instagram.com/p/<shortcode>` loses its slashes and arrives as `p<shortcode>`.
    // That is a post, not a profile — recovering it would invent "@pDTGB_1DkYrv".
    const shortcode = candidate.match(MANGLED_CONTENT_SHORTCODE_RE)
    if (shortcode && !candidate.includes(".") && /[A-Z]/.test(shortcode[1]) && /[0-9_-]/.test(shortcode[1])) {
        return reject("no_handle_in_url", `That link points at a post, not a profile — it doesn't contain a username. ${handleInputHint(platform)}`)
    }

    const result = validateCandidate(candidate, platform, true)
    if (!result.ok) {
        return reject("ambiguous_url", `We couldn't read a username out of that link. ${handleInputHint(platform)}`)
    }
    return result
}

/**
 * Turn arbitrary user input into a clean handle, or a typed rejection.
 *
 * @param input    Anything the user typed/pasted (non-strings are rejected).
 * @param platform Which platform's rules to enforce.
 */
export function normalizeSocialHandle(input: unknown, platform: SocialPlatform): HandleResult {
    if (typeof input !== "string") return reject("empty", `Enter your ${HANDLE_RULES[platform].label} username.`)
    if (input.length > MAX_INPUT_LENGTH) return reject("input_too_long", handleInputHint(platform))

    const cleaned = cleanInput(input)
    if (!cleaned) return reject("empty", `Enter your ${HANDLE_RULES[platform].label} username.`)

    const withoutAt = cleaned.replace(/^@+/, "")
    if (!withoutAt) return reject("empty", `Enter your ${HANDLE_RULES[platform].label} username.`)

    // 1. Already-mangled URL (separators stripped by the legacy paste filter).
    const mangled = withoutAt.match(MANGLED_URL_RE)
    if (mangled) {
        const mangledPlatform: SocialPlatform = mangled[1].toLowerCase().startsWith("tiktok") ? "tiktok" : "instagram"
        if (mangledPlatform !== platform) {
            return reject("platform_mismatch", `That looks like a ${HANDLE_RULES[mangledPlatform].label} link. ${handleInputHint(platform)}`)
        }
        return recoverMangled(mangled[2], platform)
    }

    // 2. Real URL — with or without a protocol.
    const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(withoutAt)
    const looksLikeUrl = hasScheme || withoutAt.includes("/") || /(^|\.)(instagram\.com|instagr\.am|tiktok\.com)($|[/?#:])/i.test(withoutAt)

    if (looksLikeUrl) {
        let parsed: URL
        try {
            parsed = new URL(hasScheme ? withoutAt : `https://${withoutAt}`)
        } catch {
            return reject("unsupported_url", handleInputHint(platform))
        }

        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return reject("unsupported_url", handleInputHint(platform))
        if (parsed.username || parsed.password) return reject("unsupported_url", handleInputHint(platform))

        const host = hostPlatform(parsed.hostname)
        if (!host) {
            return reject("unsupported_url", `We only recognise Instagram and TikTok profile links. ${handleInputHint(platform)}`)
        }
        if (host.platform !== platform) {
            return reject("platform_mismatch", `That looks like a ${HANDLE_RULES[host.platform].label} link. ${handleInputHint(platform)}`)
        }

        const segments = parsed.pathname.split("/").filter(Boolean)
        return host.platform === "instagram" ? extractFromInstagramPath(segments, platform) : extractFromTikTokPath(segments, host.subdomain, platform)
    }

    // 3. Plain handle.
    return validateCandidate(withoutAt, platform, false)
}

/** Convenience predicate — true only when the input is already a clean handle. */
export function isValidSocialHandle(input: unknown, platform: SocialPlatform): boolean {
    const result = normalizeSocialHandle(input, platform)
    return result.ok && !result.extractedFromUrl
}

/**
 * Server-side guard: returns the clean handle, or null when the input must be
 * rejected. Use `normalizeSocialHandle` directly when you need the reason.
 */
export function toSocialHandle(input: unknown, platform: SocialPlatform): string | null {
    const result = normalizeSocialHandle(input, platform)
    return result.ok ? result.handle : null
}
